import React, { useState, useEffect } from "react";
import {
    Nav, NavItem, NavLink, TabContent, TabPane,
    FormGroup, Label, Input, Badge, FormFeedback
} from "reactstrap";
import { AUTOMATION_CATEGORIES, TEMPLATE_VARIABLES } from "../../../../models/automation";
import { LoadingButton } from "../../../../components/Common";
import AutomationLogModal from "./AutomationLogModal";

/**
 * AutomationDetail
 * Painel direito do Master-Detail.
 * Tabs por gatilho na categoria selecionada, com formulário inline.
 */
const AutomationDetail = ({ triggers, categoryId, onToggleActive, onSave, isSaving }) => {

    const catInfo = AUTOMATION_CATEGORIES.find(c => c.id === categoryId) || { icon: "mdi-cog", color: "#6c757d" };
    const categoryTriggers = triggers.filter(t => t.category === categoryId);

    const [activeTabId, setActiveTabId] = useState("");
    const [formData, setFormData] = useState({});
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [logTriggerId, setLogTriggerId] = useState("");
    const [logTriggerLabel, setLogTriggerLabel] = useState("");

    // Selecionar primeira tab ao mudar de categoria
    useEffect(() => {
        if (categoryTriggers.length > 0) {
            const firstId = categoryTriggers[0].id;
            setActiveTabId(firstId);
            initForm(categoryTriggers[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    const initForm = (trigger) => {
        setFormData({
            triggerId: trigger.id,
            docId: trigger.docId || null,
            name: trigger.name || trigger.label,
            channel: trigger.channel || "whatsapp",
            message: trigger.message || "",
            delayDays: trigger.delayDays || 0,
            delayHours: trigger.delayHours || 0,
            scheduledTime: trigger.scheduledTime || "08:00",
            active: trigger.active || false,
        });
    };

    const handleTabClick = (trigger) => {
        setActiveTabId(trigger.id);
        initForm(trigger);
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const insertVariable = (variable) => {
        const textarea = document.getElementById("automation-message");
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const current = formData.message || "";
        const newValue = current.substring(0, start) + variable + current.substring(end);
        handleFieldChange("message", newValue);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + variable.length, start + variable.length);
        }, 0);
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    const activeTrigger = categoryTriggers.find(t => t.id === activeTabId);

    if (!categoryId) {
        return (
            <div className="border border-top rounded-0 h-100">
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                    <div className="text-center text-muted">
                        <i className="mdi mdi-arrow-left-bold font-size-48 opacity-25 mb-3 d-block"></i>
                        <p className="font-size-14 mb-0">Selecione uma categoria para ver os gatilhos.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
    <>
        <div className="border border-top rounded-0 h-100 overflow-hidden bg-white">
            {/* Header da categoria */}
            <div className="bg-white border-bottom py-3 px-4 rounded-0">
                <div className="d-flex align-items-center">
                    <i className={`mdi ${catInfo.icon} font-size-24 me-3 text-primary`}></i>
                    <div>
                        <h6 className="mb-0 fw-bold text-dark">{categoryId}</h6>
                        <p className="text-muted small mb-0">
                            {categoryTriggers.filter(t => t.active).length} de {categoryTriggers.length} ativa{categoryTriggers.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs de gatilhos */}
            <Nav tabs className="px-3 pt-3 border-bottom-0 rounded-0">
                {categoryTriggers.map(trigger => (
                    <NavItem key={trigger.id}>
                        <NavLink
                            className={`cursor-pointer d-flex align-items-center gap-1 rounded-0 ${activeTabId === trigger.id ? 'active fw-bold' : 'text-muted'}`}
                            onClick={() => handleTabClick(trigger)}
                            style={{ fontSize: '13px', cursor: 'pointer' }}
                        >
                            <i className={`mdi ${trigger.icon} font-size-14`}></i>
                            <span className="text-truncate" style={{ maxWidth: '130px' }}>{trigger.label}</span>
                            {trigger.active && (
                                <span
                                    className="ms-1 rounded-circle d-inline-block"
                                    style={{ width: '7px', height: '7px', backgroundColor: '#198754' }}
                                ></span>
                            )}
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>

            {/* Conteúdo da tab */}
            <TabContent activeTab={activeTabId} className="p-4">
                {activeTrigger && (
                    <TabPane tabId={activeTabId}>
                        {/* Descrição do gatilho */}
                        <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
                            <div className="d-flex align-items-center">
                                <i className={`mdi ${activeTrigger.icon} font-size-28 me-3 text-primary`}></i>
                                <div>
                                    <h6 className="mb-0 fw-bold">{activeTrigger.label}</h6>
                                    <p className="text-muted small mb-0">{activeTrigger.description}</p>
                                </div>
                            </div>

                            {/* Switch ativar/desativar + Botão Log */}
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 rounded-0"
                                    onClick={() => {
                                        setLogTriggerId(activeTrigger.id);
                                        setLogTriggerLabel(activeTrigger.label);
                                        setLogModalOpen(true);
                                    }}
                                    title="Ver histórico de envios"
                                >
                                    <i className="mdi mdi-history font-size-14"></i>
                                    <span className="d-none d-md-inline">Histórico</span>
                                </button>
                                <span className={`font-size-12 fw-semibold ${formData.active ? 'text-success' : 'text-muted'}`}>
                                    {formData.active ? 'Ativo' : 'Inativo'}
                                </span>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={formData.active}
                                        onChange={() => {
                                            const newVal = !formData.active;
                                            handleFieldChange("active", newVal);
                                            // onToggleActive(activeTrigger) - removido para salvar apenas no botão "Salvar"
                                        }}
                                        style={{ cursor: 'pointer', width: '2.5em', height: '1.2em' }}
                                    />
                                </div>
                            </div>
                        </div>


                        <div className="mb-3">
                            {/* Delay (se o trigger suporta) */}
                            {activeTrigger.supportsDelay && (
                                <FormGroup className="mb-3">
                                    <Label className="fw-bold font-size-13 d-block">{activeTrigger.delayLabel || "Dias"}</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={formData.delayDays}
                                        onChange={e => handleFieldChange("delayDays", Number(e.target.value))}
                                        className="rounded-0 py-2 border"
                                    />
                                </FormGroup>
                            )}

                            {/* Horário de envio (se configurável) */}
                            {activeTrigger.supportsScheduledTime && (
                                <FormGroup className="mb-2">
                                    <Label className="fw-bold font-size-13 d-block">
                                        <i className="mdi mdi-clock-outline me-1"></i>
                                        Horário de Envio
                                    </Label>
                                    <Input
                                        type="time"
                                        value={formData.scheduledTime || "08:00"}
                                        onChange={e => handleFieldChange("scheduledTime", e.target.value)}
                                        className="rounded-0 py-2 border"
                                    />
                                </FormGroup>
                            )}
                        </div>

                        {/* Mensagem */}
                        <FormGroup className="mb-2">
                            <Label className="fw-bold font-size-13">Mensagem</Label>
                            <Input
                                type="textarea"
                                id="automation-message"
                                rows="8"
                                placeholder="Escreva a mensagem automática..."
                                value={formData.message}
                                onChange={e => handleFieldChange("message", e.target.value)}
                                className="rounded-0 py-2 border"
                                invalid={formData.message?.length > 0 && formData.message.length < 10}
                            />
                            {formData.message?.length > 0 && formData.message.length < 10 && (
                                <FormFeedback>Mínimo 10 caracteres</FormFeedback>
                            )}
                        </FormGroup>

                        {/* Variáveis */}
                        <div className="mb-4">
                            <small className="text-muted fw-bold d-block mb-2">Variáveis disponíveis (clique para inserir):</small>
                            <div className="d-flex gap-1 flex-wrap">
                                {TEMPLATE_VARIABLES.map(v => (
                                    <Badge
                                        key={v.key}
                                        color="light"
                                        className="text-dark border font-size-11 d-flex align-items-center gap-1 rounded-0"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => insertVariable(v.key)}
                                        title={`Insere: ${v.key} — Ex: ${v.example}`}
                                    >
                                        <i className="mdi mdi-plus-circle-outline font-size-12 text-primary"></i>
                                        {v.label}
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        {/* Botão salvar */}
                        <div className="d-flex justify-content-end pt-3 border-top">
                            <LoadingButton
                                color="primary"
                                className="rounded-0 px-4"
                                loading={isSaving}
                                onClick={handleSubmit}
                                disabled={!formData.message || formData.message.length < 10}
                            >
                                Salvar
                            </LoadingButton>
                        </div>
                    </TabPane>
                )}
            </TabContent>
        </div>

        {/* Modal de Logs */}
        <AutomationLogModal
            isOpen={logModalOpen}
            toggle={() => setLogModalOpen(false)}
            triggerId={logTriggerId}
            triggerLabel={logTriggerLabel}
        />
    </>
    );
};

export default AutomationDetail;
